import { useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Link } from 'react-router-dom';
import { 
  Play, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Workflow, 
  Mail, 
  Calendar, 
  Users, 
  Zap,
  RefreshCw,
  Eye,
  Save,
  Trash2,
  TestTube2,
  ArrowRight
} from 'lucide-react';
import { workflowExecutionEngine } from '@/services/workflowExecutionEngine';
import { blink } from '@/lib/blink';
import { gpt5NanoAgent, executeUserIntent } from '@/services/gpt5NanoAgent';
import { tracedEmailActions } from '@/services/tracedEmailActions';
import { apiTracer } from '@/services/apiTracer';

interface AgentTrace {
  traceId: string;
  agentName: string;
  action: string;
  apiProvider: string;
  request: any;
  response: any;
  success: boolean;
  duration: number;
  timestamp: Date;
  context?: any;
}

interface TestResult {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'success' | 'error';
  duration?: number;
  error?: string;
  data?: any;
  timestamp: Date;
  agentTraces?: AgentTrace[];
  thinking?: string;
  reasoning?: string;
}

interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  category: 'email' | 'calendar' | 'contact' | 'multi-step';
  workflow: any;
}

export default function WorkflowTestSuite() {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);

  // Workflow templates for testing
  const workflowTemplates: WorkflowTemplate[] = [
    {
      id: 'email-auto-reply',
      name: 'Auto-Reply to Urgent Emails',
      description: 'Automatically reply to emails marked as urgent',
      category: 'email',
      workflow: {
        name: 'Auto-Reply Urgent',
        trigger: { type: 'email_received', conditions: { is_urgent: true } },
        actions: [
          { 
            type: 'ai_generate_reply', 
            config: { tone: 'professional', max_length: 200 } 
          },
          { 
            type: 'send_email', 
            config: { subject_prefix: 'Re: ' } 
          }
        ]
      }
    },
    {
      id: 'calendar-prep',
      name: 'Meeting Preparation',
      description: 'Prepare briefing for upcoming meetings',
      category: 'calendar',
      workflow: {
        name: 'Meeting Prep',
        trigger: { type: 'calendar_event_upcoming', conditions: { hours_before: 2 } },
        actions: [
          { 
            type: 'extract_participants', 
            config: {} 
          },
          { 
            type: 'search_related_emails', 
            config: { days_back: 7 } 
          },
          { 
            type: 'generate_briefing', 
            config: { include_history: true } 
          },
          { 
            type: 'create_task', 
            config: { title: 'Review meeting briefing' } 
          }
        ]
      }
    },
    {
      id: 'contact-enrichment',
      name: 'Contact Intelligence Update',
      description: 'Update contact intelligence on new interactions',
      category: 'contact',
      workflow: {
        name: 'Contact Enrichment',
        trigger: { type: 'email_received', conditions: { from_new_contact: true } },
        actions: [
          { 
            type: 'extract_contact_info', 
            config: {} 
          },
          { 
            type: 'analyze_relationship', 
            config: { include_sentiment: true } 
          },
          { 
            type: 'update_contact_intelligence', 
            config: {} 
          }
        ]
      }
    },
    {
      id: 'email-triage',
      name: 'Smart Email Triage',
      description: 'Classify and route incoming emails',
      category: 'multi-step',
      workflow: {
        name: 'Email Triage',
        trigger: { type: 'email_received', conditions: {} },
        actions: [
          { 
            type: 'ai_classify_email', 
            config: { categories: ['urgent', 'action_required', 'fyi', 'spam'] } 
          },
          { 
            type: 'conditional_branch', 
            config: {
              conditions: [
                { if: 'category == urgent', then: 'notify_user' },
                { if: 'category == action_required', then: 'create_task' },
                { if: 'category == spam', then: 'move_to_spam' }
              ]
            }
          },
          { 
            type: 'apply_labels', 
            config: {} 
          }
        ]
      }
    }
  ];

  const addTestResult = useCallback((result: Partial<TestResult>) => {
    setTestResults(prev => [...prev, {
      id: `test-${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
      status: 'pending',
      ...result
    } as TestResult]);
  }, []);

  const updateTestResult = useCallback((id: string, updates: Partial<TestResult>) => {
    setTestResults(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  }, []);

  // Test 1: Basic Workflow Creation
  const testWorkflowCreation = async () => {
    const testId = `test-${Date.now()}`;
    addTestResult({ id: testId, name: 'Create Basic Workflow', status: 'running' });
    const startTime = Date.now();

    try {
      const user = await blink.auth.me();
      
      const workflow = await blink.db.workflows.create({
        id: `wf-test-${Date.now()}`,
        userId: user.id,
        name: 'Test Workflow',
        description: 'Automated test workflow',
        trigger: JSON.stringify({ type: 'email_received' }),
        actions: JSON.stringify([
          { type: 'ai_classify', config: {} }
        ]),
        isActive: '1',
        createdAt: new Date().toISOString()
      });

      updateTestResult(testId, {
        status: 'success',
        duration: Date.now() - startTime,
        data: workflow
      });
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message
      });
    }
  };

  // Test 2: Workflow Execution
  const testWorkflowExecution = async () => {
    const testId = `test-${Date.now()}`;
    addTestResult({ id: testId, name: 'Execute Workflow', status: 'running' });
    const startTime = Date.now();

    try {
      const user = await blink.auth.me();
      
      // Create a simple workflow
      const workflow = {
        id: `wf-exec-${Date.now()}`,
        userId: user.id,
        name: 'Execution Test',
        trigger: { type: 'manual' },
        actions: [
          { type: 'log', config: { message: 'Workflow executed' } }
        ],
        isActive: true
      };

      // Execute the workflow
      const result = await workflowExecutionEngine.executeWorkflow(workflow, {
        trigger: 'manual_test',
        timestamp: new Date().toISOString()
      });

      updateTestResult(testId, {
        status: 'success',
        duration: Date.now() - startTime,
        data: result
      });
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message
      });
    }
  };

  // Test 3: Email Automation Workflow
  const testEmailAutomation = async () => {
    const testId = `test-${Date.now()}`;
    addTestResult({ id: testId, name: 'Email Automation Workflow', status: 'running' });
    const startTime = Date.now();

    try {
      const user = await blink.auth.me();
      
      // Create email automation workflow
      const workflow = {
        id: `wf-email-${Date.now()}`,
        userId: user.id,
        name: 'Email Auto-Reply',
        trigger: {
          type: 'email_received',
          conditions: { is_urgent: true }
        },
        actions: [
          { 
            type: 'ai_generate_reply', 
            config: { 
              prompt: 'Generate a professional acknowledgment',
              tone: 'professional',
              from: '{{trigger.from}}',
              subject: '{{trigger.subject}}'
            } 
          },
          { 
            type: 'send_email', 
            config: { 
              to: '{{trigger.from}}',
              subject: 'Re: {{trigger.subject}}',
              body: '{{node_action-1.generatedReply}}',
              subject_prefix: 'Re: ',
              auto_reply: true
            } 
          }
        ],
        isActive: true
      };

      // Simulate workflow execution with proper context for template variables
      const mockEmailContext = {
        trigger: {
          id: 'test-email-1',
          subject: 'Urgent: Need Response',
          from: 'test@example.com',
          body: 'Please respond ASAP',
          is_urgent: true
        },
        email: {
          id: 'test-email-1',
          subject: 'Urgent: Need Response',
          sender: 'test@example.com',
          is_urgent: true
        },
        // Add userId for workflow execution
        userId: user.id
      };

      const result = await workflowExecutionEngine.executeWorkflow(workflow, mockEmailContext);

      updateTestResult(testId, {
        status: 'success',
        duration: Date.now() - startTime,
        data: result
      });
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message
      });
    }
  };

  // Test 4: Calendar Workflow
  const testCalendarWorkflow = async () => {
    const testId = `test-${Date.now()}`;
    addTestResult({ id: testId, name: 'Calendar Meeting Prep', status: 'running' });
    const startTime = Date.now();

    try {
      const user = await blink.auth.me();
      
      const workflow = {
        id: `wf-cal-${Date.now()}`,
        userId: user.id,
        name: 'Meeting Preparation',
        trigger: {
          type: 'calendar_event_upcoming',
          conditions: { hours_before: 2 }
        },
        actions: [
          { type: 'extract_participants', config: { 
            body: '{{trigger.body}}',
            subject: '{{trigger.title}}'
          } },
          { type: 'search_related_emails', config: { 
            days_back: 7,
            query: '{{trigger.title}}',
            subject: '{{trigger.title}}'
          } },
          { type: 'generate_briefing', config: { 
            include_history: true,
            maxTokens: 1500
          } }
        ],
        isActive: true
      };

      const mockCalendarContext = {
        trigger: {
          id: 'test-event-1',
          title: 'Project Review Meeting',
          body: 'Meeting agenda: Review Q4 progress and plan next steps. Attendees: john@example.com, jane@example.com',
          start: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
          attendees: ['john@example.com', 'jane@example.com']
        },
        event: {
          id: 'test-event-1',
          title: 'Project Review Meeting',
          start_time: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
          attendees: ['john@example.com', 'jane@example.com']
        },
        userId: user.id
      };

      const result = await workflowExecutionEngine.executeWorkflow(workflow, mockCalendarContext);

      updateTestResult(testId, {
        status: 'success',
        duration: Date.now() - startTime,
        data: result
      });
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message
      });
    }
  };

  // Test 5: Multi-Step Workflow
  const testMultiStepWorkflow = async () => {
    const testId = `test-${Date.now()}`;
    addTestResult({ id: testId, name: 'Multi-Step Email Triage', status: 'running' });
    const startTime = Date.now();

    try {
      const user = await blink.auth.me();
      
      const workflow = {
        id: `wf-multi-${Date.now()}`,
        userId: user.id,
        name: 'Email Triage',
        trigger: { type: 'email_received' },
        actions: [
          { 
            type: 'ai_classify_email', 
            config: { 
              categories: ['urgent', 'action_required', 'fyi', 'spam'],
              subject: '{{trigger.subject}}',
              body: '{{trigger.body}}',
              from: '{{trigger.from}}'
            } 
          },
          { 
            type: 'conditional_branch', 
            config: {
              condition: {
                field: 'node_action-1.classification.category',
                operator: 'equals',
                value: 'urgent'
              }
            }
          },
          { type: 'apply_labels', config: {
            emailId: '{{trigger.id}}',
            labels: '{{node_action-1.classification.suggestedTags}}'
          } }
        ],
        isActive: true
      };

      const mockEmailContext = {
        trigger: {
          id: 'test-email-2',
          subject: 'Important: Review Required',
          body: 'Please review the attached document by EOD.',
          from: 'manager@example.com'
        },
        email: {
          id: 'test-email-2',
          subject: 'Important: Review Required',
          body: 'Please review the attached document by EOD.',
          sender: 'manager@example.com'
        },
        userId: user.id
      };

      const result = await workflowExecutionEngine.executeWorkflow(workflow, mockEmailContext);

      updateTestResult(testId, {
        status: 'success',
        duration: Date.now() - startTime,
        data: result
      });
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message
      });
    }
  };

  // Test 6: Agent-Based Email Test
  const testAgentEmailAction = async () => {
    const testId = `test-${Date.now()}`;
    addTestResult({ id: testId, name: 'Agent Email Search with Traces', status: 'running' });
    const startTime = Date.now();

    try {
      const user = await blink.auth.me();
      
      // Note: apiTracer clears traces automatically per request, no manual clear needed
      
      // Execute agent action
      const result = await executeUserIntent('Show me my recent urgent emails');
      
      // Get traces
      const traces = apiTracer.getTracesForUser(user.id).map(trace => ({
        traceId: trace.traceId,
        agentName: trace.agentName,
        action: trace.action,
        apiProvider: trace.apiProvider,
        request: trace.request,
        response: trace.response,
        success: trace.success,
        duration: trace.duration,
        timestamp: new Date(trace.timestamp),
        context: trace.context
      }));

      updateTestResult(testId, {
        status: 'success',
        duration: Date.now() - startTime,
        data: result,
        agentTraces: traces,
        thinking: result.agent_response?.thinking,
        reasoning: result.agent_response?.reasoning
      });
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message
      });
    }
  };

  // Test 7: Workflow Template Execution
  const testTemplateExecution = async (template: WorkflowTemplate) => {
    const testId = `test-${Date.now()}`;
    addTestResult({ id: testId, name: `Template: ${template.name}`, status: 'running' });
    const startTime = Date.now();

    try {
      const user = await blink.auth.me();
      
      const workflow = {
        ...template.workflow,
        id: `wf-tpl-${Date.now()}`,
        userId: user.id,
        isActive: true
      };

      // Create appropriate mock context based on template category
      let mockContext: any = { 
        trigger: { 
          type: workflow.trigger.type,
          id: 'test-trigger-1',
          subject: 'Test Email Subject',
          from: 'sender@example.com',
          body: 'This is a test email body for workflow execution.',
          is_urgent: true
        },
        userId: user.id
      };
      
      if (template.category === 'email') {
        mockContext.email = {
          id: 'test-email',
          subject: 'Test Email Subject',
          from: 'sender@example.com',
          sender: 'sender@example.com',
          body: 'This is a test email body for workflow execution.',
          is_urgent: true
        };
      } else if (template.category === 'calendar') {
        mockContext.event = {
          id: 'test-event',
          title: 'Test Meeting',
          body: 'Meeting agenda: Discuss project progress',
          start_time: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
          attendees: ['john@example.com', 'jane@example.com']
        };
        mockContext.trigger = {
          ...mockContext.trigger,
          title: 'Test Meeting',
          body: 'Meeting agenda: Discuss project progress',
          attendees: ['john@example.com', 'jane@example.com']
        };
      }

      const result = await workflowExecutionEngine.executeWorkflow(workflow, mockContext);

      updateTestResult(testId, {
        status: 'success',
        duration: Date.now() - startTime,
        data: result
      });
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message
      });
    }
  };

  // Run all tests
  const runAllTests = async () => {
    setIsRunning(true);
    setTestResults([]);

    await testWorkflowCreation();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testWorkflowExecution();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testEmailAutomation();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testCalendarWorkflow();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testMultiStepWorkflow();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testAgentEmailAction();
    await new Promise(resolve => setTimeout(resolve, 500));

    setIsRunning(false);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: TestResult['status']) => {
    const variants = {
      success: 'default',
      error: 'destructive',
      running: 'secondary',
      pending: 'outline'
    };
    return <Badge variant={variants[status] as any}>{status}</Badge>;
  };

  const successCount = testResults.filter(r => r.status === 'success').length;
  const errorCount = testResults.filter(r => r.status === 'error').length;
  const runningCount = testResults.filter(r => r.status === 'running').length;

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur">
        <div className="container mx-auto p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Workflow className="w-8 h-8 text-primary" />
                Live Workflow Test Suite
              </h1>
              <p className="text-muted-foreground mt-1">
                Comprehensive testing for workflow engine and automation with agent trace monitoring
              </p>
              <div className="mt-3">
                <Link to="/live-email-test">
                  <Button variant="outline" size="sm" className="gap-2">
                    <TestTube2 className="w-4 h-4" />
                    Go to Email Test Suite
                    <ArrowRight className="w-3 h-3" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={runAllTests}
                disabled={isRunning}
                className="gap-2"
              >
                {isRunning ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Running Tests...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Run All Tests
                  </>
                )}
              </Button>
              <Button
                onClick={clearResults}
                variant="outline"
                disabled={testResults.length === 0}
                className="gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Clear
              </Button>
            </div>
          </div>

          {/* Info Banner */}
          <Card className="mt-4 border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="bg-blue-500 text-white rounded-full p-2">
                  <Workflow className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    Workflow Test Suite
                  </h4>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    Tests workflow engine, automation templates, and the unified GPT-5 Nano Agent with full trace monitoring.
                    Includes email automation, calendar workflows, and multi-step processes.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          {testResults.length > 0 && (
            <div className="mt-4 flex gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium">{successCount} Passed</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm font-medium">{errorCount} Failed</span>
              </div>
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium">{runningCount} Running</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <Tabs defaultValue="tests" className="h-full flex flex-col">
          <div className="border-b bg-card/30">
            <div className="container mx-auto">
              <TabsList className="bg-transparent">
                <TabsTrigger value="tests" className="gap-2">
                  <Play className="w-4 h-4" />
                  Test Runner
                </TabsTrigger>
                <TabsTrigger value="templates" className="gap-2">
                  <Workflow className="w-4 h-4" />
                  Templates
                </TabsTrigger>
                <TabsTrigger value="results" className="gap-2">
                  <Eye className="w-4 h-4" />
                  Results ({testResults.length})
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            <div className="container mx-auto p-6">
              <TabsContent value="tests" className="mt-0">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {/* Individual Test Cards */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Zap className="w-5 h-5" />
                        Workflow Creation
                      </CardTitle>
                      <CardDescription>
                        Test basic workflow creation in database
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        onClick={testWorkflowCreation}
                        disabled={isRunning}
                        className="w-full"
                      >
                        Run Test
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Play className="w-5 h-5" />
                        Workflow Execution
                      </CardTitle>
                      <CardDescription>
                        Test workflow execution engine
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        onClick={testWorkflowExecution}
                        disabled={isRunning}
                        className="w-full"
                      >
                        Run Test
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        Email Automation
                      </CardTitle>
                      <CardDescription>
                        Test email auto-reply workflow
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        onClick={testEmailAutomation}
                        disabled={isRunning}
                        className="w-full"
                      >
                        Run Test
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        Calendar Workflow
                      </CardTitle>
                      <CardDescription>
                        Test meeting preparation workflow
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        onClick={testCalendarWorkflow}
                        disabled={isRunning}
                        className="w-full"
                      >
                        Run Test
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Multi-Step Workflow
                      </CardTitle>
                      <CardDescription>
                        Test complex email triage workflow
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        onClick={testMultiStepWorkflow}
                        disabled={isRunning}
                        className="w-full"
                      >
                        Run Test
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Zap className="w-5 h-5" />
                        Agent Email Action
                      </CardTitle>
                      <CardDescription>
                        Test GPT-5 Nano Agent with full traces
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        onClick={testAgentEmailAction}
                        disabled={isRunning}
                        className="w-full"
                      >
                        Run Test
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="templates" className="mt-0">
                <div className="grid gap-4 md:grid-cols-2">
                  {workflowTemplates.map(template => (
                    <Card 
                      key={template.id}
                      className={selectedTemplate?.id === template.id ? 'border-primary' : ''}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="flex items-center gap-2">
                              {template.category === 'email' && <Mail className="w-5 h-5" />}
                              {template.category === 'calendar' && <Calendar className="w-5 h-5" />}
                              {template.category === 'contact' && <Users className="w-5 h-5" />}
                              {template.category === 'multi-step' && <Workflow className="w-5 h-5" />}
                              {template.name}
                            </CardTitle>
                            <CardDescription className="mt-2">
                              {template.description}
                            </CardDescription>
                          </div>
                          <Badge>{template.category}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="text-sm">
                            <div className="font-medium text-muted-foreground">Trigger:</div>
                            <code className="text-xs bg-muted p-1 rounded">
                              {template.workflow.trigger.type}
                            </code>
                          </div>
                          <div className="text-sm">
                            <div className="font-medium text-muted-foreground">Actions:</div>
                            <div className="text-xs text-muted-foreground">
                              {template.workflow.actions.length} step(s)
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2">
                            <Button
                              onClick={() => testTemplateExecution(template)}
                              disabled={isRunning}
                              size="sm"
                              className="flex-1"
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Test
                            </Button>
                            <Button
                              onClick={() => setSelectedTemplate(template)}
                              variant="outline"
                              size="sm"
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Template Details */}
                {selectedTemplate && (
                  <Card className="mt-4">
                    <CardHeader>
                      <CardTitle>Template Details: {selectedTemplate.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="text-xs bg-muted p-4 rounded overflow-auto">
                        {JSON.stringify(selectedTemplate.workflow, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="results" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Test Results</CardTitle>
                    <CardDescription>
                      Detailed results from workflow tests
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {testResults.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Workflow className="w-12 h-12 mx-auto mb-4 opacity-20" />
                        <p>No test results yet</p>
                        <p className="text-sm">Run tests to see results here</p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[600px]">
                        <div className="space-y-4">
                          {testResults.map((result, index) => (
                            <div key={result.id}>
                              {index > 0 && <Separator className="my-4" />}
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {getStatusIcon(result.status)}
                                    <span className="font-medium">{result.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {getStatusBadge(result.status)}
                                    {result.duration && (
                                      <Badge variant="outline">
                                        {result.duration}ms
                                      </Badge>
                                    )}
                                  </div>
                                </div>

                                <div className="text-xs text-muted-foreground">
                                  {result.timestamp.toLocaleString()}
                                </div>

                                {result.error && (
                                  <div className="bg-destructive/10 border border-destructive/20 rounded p-3">
                                    <div className="font-medium text-destructive text-sm mb-1">
                                      Error
                                    </div>
                                    <code className="text-xs text-destructive">
                                      {result.error}
                                    </code>
                                  </div>
                                )}

                                {result.thinking && (
                                  <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded p-3">
                                    <div className="font-medium text-blue-900 dark:text-blue-100 text-sm mb-1 flex items-center gap-2">
                                      <Zap className="w-4 h-4" />
                                      Agent Thinking
                                    </div>
                                    <p className="text-xs text-blue-800 dark:text-blue-200">
                                      {result.thinking}
                                    </p>
                                  </div>
                                )}

                                {result.reasoning && (
                                  <div className="bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded p-3">
                                    <div className="font-medium text-purple-900 dark:text-purple-100 text-sm mb-1 flex items-center gap-2">
                                      <Workflow className="w-4 h-4" />
                                      Agent Reasoning
                                    </div>
                                    <p className="text-xs text-purple-800 dark:text-purple-200">
                                      {result.reasoning}
                                    </p>
                                  </div>
                                )}

                                {result.agentTraces && result.agentTraces.length > 0 && (
                                  <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded p-3">
                                    <div className="font-medium text-green-900 dark:text-green-100 text-sm mb-2 flex items-center gap-2">
                                      <Eye className="w-4 h-4" />
                                      Agent Traces ({result.agentTraces.length})
                                    </div>
                                    <div className="space-y-2">
                                      {result.agentTraces.map((trace, idx) => (
                                        <div key={trace.traceId} className="border-l-2 border-green-400 pl-2">
                                          <div className="flex items-center gap-2 text-xs">
                                            <Badge variant={trace.success ? "default" : "destructive"} className="text-[10px]">
                                              {trace.agentName}
                                            </Badge>
                                            <span className="text-green-700 dark:text-green-300 font-mono">
                                              {trace.action}
                                            </span>
                                            <span className="text-green-600 dark:text-green-400">
                                              {trace.duration}ms
                                            </span>
                                            <Badge variant="outline" className="text-[10px]">
                                              {trace.apiProvider}
                                            </Badge>
                                          </div>
                                          {trace.context?.userIntent && (
                                            <div className="text-[10px] text-green-700 dark:text-green-300 mt-1">
                                              Intent: {trace.context.userIntent}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {result.data && (
                                  <details className="bg-muted rounded p-3">
                                    <summary className="cursor-pointer text-sm font-medium mb-2">
                                      View Data
                                    </summary>
                                    <pre className="text-xs overflow-auto max-h-40">
                                      {JSON.stringify(result.data, null, 2)}
                                    </pre>
                                  </details>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
